import React from 'react';

export type ChipVariant = 'filter' | 'display' | 'dashed' | 'mono';
export type ChipSize = 'sm' | 'md' | 'lg';

export interface ChipProps {
  selected?: boolean;
  mono?: boolean;
  variant?: ChipVariant;
  size?: ChipSize;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const Chip: React.FC<ChipProps> = ({
  selected = false,
  mono = false,
  variant = 'filter',
  size = 'md',
  icon,
  disabled = false,
  onClick,
  children,
  className = '',
  'data-testid': dataTestId
}) => {
  const isDisplay = variant === 'display';
  const isInteractive = !isDisplay && Boolean(onClick);
  const isMono = mono || variant === 'mono';

  const sizeClasses: Record<ChipSize, string> = {
    sm: 'h-7 max-md:h-11 max-md:min-w-11 px-2.5 text-[11px]',
    md: 'h-8 max-md:h-11 max-md:min-w-11 px-3 text-xs',
    lg: 'h-9 max-md:h-11 max-md:min-w-11 px-3.5 text-sm'
  };

  const getVariantClasses = () => {
    if (variant === 'dashed') {
      return 'border border-dashed border-base-300 bg-transparent text-base-content/60 hover:border-base-content/40';
    }
    if (selected) {
      return 'border border-primary bg-primary/15 text-primary-soft font-semibold';
    }
    return 'border border-base-300 bg-neutral text-neutral-content/80 hover:border-base-content/30';
  };

  const hitTargetClasses =
    isInteractive && (size === 'sm' || size === 'md')
      ? "before:content-[''] before:absolute before:-inset-y-1.5 before:inset-x-0 relative"
      : '';

  const commonClasses = `inline-flex items-center justify-center gap-1.5 rounded-full select-none transition ${
    isMono ? 'font-mono tabular-nums' : 'font-medium'
  } ${sizeClasses[size]} ${getVariantClasses()} ${
    isInteractive
      ? 'cursor-pointer active:scale-95 focus-visible:outline-none focus-visible:ring-2 ring-primary ring-offset-2 ring-offset-base-100'
      : 'cursor-default'
  } ${hitTargetClasses} ${disabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''} ${className}`;

  const innerContent = (
    <>
      {icon && <span className="shrink-0">{icon}</span>}
      <span dir={isMono ? 'ltr' : undefined}>{children}</span>
    </>
  );

  if (isDisplay) {
    return (
      <span
        className={commonClasses}
        dir={isMono ? 'ltr' : undefined}
        data-testid={dataTestId}
      >
        {innerContent}
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={isInteractive ? onClick : undefined}
      aria-pressed={variant === 'filter' ? selected : undefined}
      className={commonClasses}
      dir={isMono ? 'ltr' : undefined}
      data-testid={dataTestId}
    >
      {innerContent}
    </button>
  );
};

Chip.displayName = 'Chip';
